import { useQuery } from '@tanstack/react-query';
import { isWaitingOn, SERVICE_WAITING_ON } from '@technic/contracts';
import { serviceWaitingCountQuery } from '@entities/service-request';
import { useAuth } from '@entities/session';

/**
 * Бейдж «ждёт меня» на разделе оргтехники (ADR 0085, Р39).
 *
 * Счётчик спрашивается **не у всех, кто видит раздел**, и это не экономия, а суть: ход заявки
 * стоит за теми, у кого в цикле есть шаг. У заказчика — штаба и ролей отдела, то есть у основной
 * массы видящих пункт меню, — шага нет намеренно: заявку принимает оператор, а не тот, кто её
 * завёл. Сервер ответил бы такой учётке нулём всегда, и бейдж превратился бы в обещание
 * «непрочитанного», которого в портале нет вовсе, — плюс лишний запрос на каждый вход.
 *
 * **Своего перечня сторон здесь нет** (Р3). Прежде условие перечисляло права по одному, и новая
 * сторона `approval` — согласование объёма работ — молча прошла бы мимо бейджа: сервер считал бы
 * такие заявки в «Ждут меня», а кружок на разделе их не показывал. Поэтому стороны берутся из
 * `SERVICE_WAITING_ON`, а «моя ли она» отвечает `isWaitingOn` контрактов — тот же предикат, каким
 * подписан столбец состояния и отобрана очередь на сервере. Заведут шаг заказчика — бейдж
 * появится у него сам, без правки этого файла.
 */
export function useServiceWaitingCount(): number {
  const { user, can } = useAuth();
  const inServiceLoop =
    SERVICE_WAITING_ON.some((side) => isWaitingOn(user, side, can)) ||
    /*
     * Поимённый исполнитель — вторым слагаемым, потому что по субъекту он не виден и виден быть не
     * может: «я в списке назначенных» — свойство заявки, а не учётки, и в очередь он попадает
     * соединением с `service_request_executors` уже на сервере (`waitingOnMeWhere`). Право
     * `serviceRequests.execute` выдаётся ровно тем, кто работает исполнителем, и заявки, ждущие их
     * самих, сервер считает обеими сторонами сразу — `service` («принять в работу», «выполнить») и
     * `approval` (подпись назначенного, ответ В2). Без этой строки бейджа не было бы у того, кому
     * заявка адресована поимённо, — то есть ровно у того, кто её и держит.
     */
    can('serviceRequests.execute');
  const { data } = useQuery({
    ...serviceWaitingCountQuery(),
    enabled: inServiceLoop,
  });
  return data?.count ?? 0;
}
